import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Coins, LifeBuoy, LogOut, WashingMachine, ArrowRight, Inbox, Send, ArrowLeft, Ban, ShieldAlert } from "lucide-react";
import { api, errMsg, fmtDateTime, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Section, Chip } from "@/components/common";
import { usePrices } from "@/pages/Pricing";

function Credits() {
  const { t } = useT();
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/me/credits").then((r) => setC(r.data)).catch(() => {}); }, []);
  return (
    <Section title={<span className="flex items-center gap-2"><Coins size={20} aria-hidden="true" />{t("acc.credits")}</span>} testId="credits-section">
      <p className="text-4xl font-extrabold" data-testid="credits-balance">{c?.balance ?? 0}</p>
      {c && !c.live && <p className="w-muted mt-1" data-testid="credits-flag-off">{t("acc.credits_off")}</p>}
    </Section>
  );
}

function Support() {
  const { t, lang } = useT();
  const [f, setF] = useState({ subject: "", body: "" });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/support/tickets").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/support/tickets", f); toast.success("Anfrage gesendet"); setF({ subject: "", body: "" }); load(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Section title={<span className="flex items-center gap-2"><LifeBuoy size={20} aria-hidden="true" />{t("acc.support")}</span>} testId="support-section">
      <form onSubmit={submit} className="space-y-3">
        <label className="block"><span className="w-label">{t("acc.subject")}</span><input className="w-input" required minLength={3} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="ticket-subject" /></label>
        <label className="block"><span className="w-label">{t("acc.message")}</span><textarea className="w-input !h-28 py-3" required minLength={10} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="ticket-body" /></label>
        <button className="w-btn w-btn-primary" disabled={busy} data-testid="ticket-submit-btn">{busy ? t("common.loading") : t("acc.send_ticket")}</button>
      </form>
      <ul className="mt-5 space-y-2" data-testid="ticket-list">
        {items.map((x) => (
          <li key={x.id} className="w-card p-3 flex flex-wrap justify-between gap-2"><span className="font-semibold">{x.subject}</span>
            <span className="flex gap-2"><Chip tone="info">{x.category}</Chip><Chip>{x.status}</Chip><span className="w-muted">{fmtDateTime(x.created_at, lang)}</span></span></li>
        ))}
      </ul>
    </Section>
  );
}

function WashheldCard({ user, refresh }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [risk, setRisk] = useState(false);
  const px = usePrices();
  const activate = async () => {
    setBusy(true);
    try { await api.post("/provider/me/activate", { accept_risk: risk }); await refresh(); toast.success("Waschheld-Modus aktiviert – leg direkt los!"); nav("/provider"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Section title={<span className="flex items-center gap-2"><WashingMachine size={20} aria-hidden="true" /> Waschheld werden</span>} testId="washheld-section">
      {user.has_provider ? (
        <>
          <p className="w-muted mb-4">Du bist als Waschheld aktiviert. Verwalte Aufträge, Profil und Zeitfenster in deinem Dashboard.</p>
          <button className="w-btn w-btn-primary" onClick={() => nav("/provider")} data-testid="open-provider-dashboard">Zum Waschheld-Dashboard <ArrowRight size={18} aria-hidden="true" /></button>
        </>
      ) : (
        <>
          <p className="w-muted mb-4">Mit demselben Konto nebenbei Geld verdienen: Zeitfenster öffnen, Wäsche übernehmen, Code scannen. Der Waschpreis ist überall fix ({eur(px?.base ?? 2.99)}/kg) – du überzeugst mit Leistung und Bewertungen.</p>
          <label className="flex items-start gap-3 text-sm cursor-pointer rounded-2xl border border-amber-300 bg-amber-50 p-4 mb-4" data-testid="provider-risk-ack-label">
            <input type="checkbox" className="h-5 w-5 mt-0.5 accent-[var(--w-mint-ink)]" checked={risk} onChange={(e) => setRisk(e.target.checked)} data-testid="provider-accept-risk" />
            <span className="leading-relaxed"><span className="flex items-center gap-1.5 font-bold text-amber-900 mb-1"><ShieldAlert size={16} aria-hidden="true" /> Eigenverantwortung &amp; Pflichten</span>Ich bestätige, dass ich meine Tätigkeit auf eigene Verantwortung ausübe. Ich bin selbst dafür verantwortlich, meine steuerlichen, gewerblichen, sozialversicherungsrechtlichen und sonstigen gesetzlichen Pflichten zu prüfen und einzuhalten. Mir ist bekannt, dass Washly keine individuelle Steuer- oder Rechtsberatung erbringt und meine persönliche Haftung nicht übernimmt.</span>
          </label>
          <button className="w-btn w-btn-primary" onClick={activate} disabled={busy || !risk} data-testid="become-provider-btn"><WashingMachine size={18} aria-hidden="true" />{busy ? "Aktiviere…" : "Als Waschheld anmelden"}</button>
        </>
      )}
    </Section>
  );
}

function Postfach() {
  const [threads, setThreads] = useState(null);
  const [open, setOpen] = useState(null);
  const [compose, setCompose] = useState(false);
  const load = () => api.get("/inbox/threads").then((r) => setThreads(r.data)).catch(() => setThreads([]));
  useEffect(() => { load(); }, []);
  if (open) return <Thread tid={open} onBack={() => { setOpen(null); load(); }} />;
  return (
    <Section title={<span className="flex items-center gap-2"><Inbox size={20} aria-hidden="true" /> Postfach</span>} testId="postfach-section"
      action={<button className="w-btn w-btn-primary !min-h-[40px] !px-4" onClick={() => setCompose(true)} data-testid="postfach-new-btn">Neue Nachricht</button>}>
      <p className="w-muted text-sm mb-3">Schreib uns direkt – wir antworten hier im Postfach.</p>
      <div className="space-y-2" data-testid="postfach-threads">
        {threads === null && <p className="w-muted">Lädt…</p>}
        {threads?.length === 0 && <p className="w-muted">Noch keine Nachrichten. Starte eine neue Unterhaltung.</p>}
        {threads?.map((t) => (
          <button key={t.id} className="w-card p-3 w-full text-left flex items-center gap-3 w-hover-lift" onClick={() => setOpen(t.id)} data-testid={`postfach-thread-${t.id}`}>
            <div className="flex-1 min-w-0"><p className="font-semibold truncate">{t.subject}</p><p className="w-muted text-sm truncate">{t.last_preview}</p></div>
            {t.unread_user > 0 && <span className="w-chip bg-[var(--w-error)] text-white">{t.unread_user} neu</span>}
            <span className="w-muted text-xs whitespace-nowrap">{fmtDateTime(t.last_at)}</span>
          </button>
        ))}
      </div>
      <ComposeDialog open={compose} onClose={(created) => { setCompose(false); if (created) load(); }} onOpen={(id) => { setCompose(false); setOpen(id); }} />
    </Section>
  );
}

function ComposeDialog({ open, onClose, onOpen }) {
  const [f, setF] = useState({ subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ subject: "", body: "" }); }, [open]);
  if (!open) return null;
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/inbox/threads", f); toast.success("Nachricht gesendet"); onOpen(data.id); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => onClose(false)} data-testid="postfach-compose">
      <form className="w-card p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()} onSubmit={send}>
        <h3 className="font-bold text-lg">Neue Nachricht an Washly</h3>
        <label className="block"><span className="w-label">Betreff</span><input className="w-input" required minLength={2} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="compose-subject" /></label>
        <label className="block"><span className="w-label">Nachricht</span><textarea className="w-input !h-28 py-3" required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="compose-body" /></label>
        <div className="flex gap-2"><button type="button" className="w-btn w-btn-secondary flex-1" onClick={() => onClose(false)}>Abbrechen</button>
          <button className="w-btn w-btn-primary flex-1" disabled={busy} data-testid="compose-send">{busy ? "Sende…" : "Senden"}</button></div>
      </form>
    </div>
  );
}

function Thread({ tid, onBack }) {
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const load = () => api.get(`/inbox/threads/${tid}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView(); }, [data]);
  const send = async (e) => {
    e.preventDefault(); if (!body.trim()) return; setBusy(true);
    try { await api.post(`/inbox/threads/${tid}/messages`, { body }); setBody(""); await load(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Section title={<button className="flex items-center gap-2" onClick={onBack} data-testid="thread-back"><ArrowLeft size={18} aria-hidden="true" /> {data?.thread?.subject || "Unterhaltung"}</button>} testId="postfach-thread-view">
      <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="thread-messages">
        {data?.messages?.map((m) => {
          const mine = m.sender_role === "user";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${mine ? "bg-mint text-mint-on" : "bg-white border border-line"}`}>
                <p className="text-xs font-semibold opacity-70 mb-0.5">{mine ? "Du" : `${m.sender_name} · Washly`}</p>
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                <p className="text-xs opacity-60 mt-1">{fmtDateTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="mt-4 flex gap-2">
        <textarea className="w-input !h-12 py-3 flex-1" placeholder="Antwort schreiben…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="thread-reply-input" />
        <button className="w-btn w-btn-primary" disabled={busy} data-testid="thread-reply-send"><Send size={18} aria-hidden="true" /></button>
      </form>
    </Section>
  );
}

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const { t, lang, setLang } = useT();
  const nav = useNavigate();
  const changeLang = (l) => { setLang(l); api.put("/auth/language", { language: l }).catch(() => {}); };
  const canBeProvider = !["admin", "support", "finance"].includes(user.role);
  return (
    <div className="w-container py-8 grid lg:grid-cols-2 gap-6">
      <div className="space-y-6">
        {user.blocked && (
          <div className="w-card p-5 border-[var(--w-error)]/40 bg-[var(--w-error-soft)]" data-testid="account-block-notice">
            <p className="flex items-center gap-2 font-bold text-[var(--w-error)]"><Ban size={18} aria-hidden="true" /> Dein Konto ist gesperrt</p>
            <p className="w-muted text-sm mt-1">{user.block_reason || "Buchungen sind vorübergehend nicht möglich."} Für mehr Infos schreib uns einfach unten im Postfach – wir melden uns.</p>
          </div>
        )}
        <Section title={t("acc.title")} testId="account-section">
          <p className="text-xl font-bold" data-testid="account-name">{user.name}</p>
          <p className="w-muted">{user.email} · {user.role}</p>
          <div className="mt-4"><span className="w-label">{t("acc.lang")}</span>
            <div className="flex gap-2">{["de", "en"].map((l) => <button key={l} aria-pressed={lang === l} className={`w-btn ${lang === l ? "w-btn-primary" : "w-btn-secondary"}`} onClick={() => changeLang(l)} data-testid={`account-lang-${l}`}>{l === "de" ? "Deutsch" : "English"}</button>)}</div></div>
          <button className="w-btn w-btn-secondary mt-6" onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn"><LogOut size={18} aria-hidden="true" />{t("acc.logout")}</button>
        </Section>
        {canBeProvider && <WashheldCard user={user} refresh={refresh} />}
        {user.role === "customer" && <Credits />}
      </div>
      <div className="space-y-6">
        <Postfach />
        <Support />
      </div>
    </div>
  );
}
